import React, { useEffect, useMemo, useState, useCallback, useRef } from "react";
import { Container, Text, Graphics } from "@pixi/react";
import { TextStyle, TextMetrics } from "pixi.js";
import { useSelector, useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";
import anime from "animejs";
import { closeInfoPopup, setUserInterfaceDisabled } from "../../../../State/actions";
import { selectInfoPopupActive, selectInfoPopupKey, selectInfoPopupData } from "../../../../State/selectors";
import { useDimensions } from "../../../../Contexts/DimensionsContext";
import { FONT_CONFIG, TEXT_STYLE_CONFIG } from "../../../../textureConfigs";
import { PixiSettings } from "../../../../pixiSettings";
import { soundManager } from "../../../../SoundManager";
import { SOUND_NAME } from "../../../../sounds";

// Development mode - set to true to test popup
const DEV_MODE = false;

// Overlay settings
const OVERLAY_BACKGROUND_ALPHA = 0.4;
const OVERLAY_BACKGROUND_COLOR = 0x000000;
const OVERLAY_FADE_DURATION = 300;

// Popup animation settings
const POPUP_SCALE_DURATION = 400;
const POPUP_SCALE_EASING = "easeOutBack";
const POPUP_SCALE_DELAY = 150; // Delay popup scale until overlay starts fading

// Popup panel settings
const PANEL_BACKGROUND_COLOR = 0x000000;
const PANEL_BACKGROUND_ALPHA = 0.7;

// Info popup configuration - Unified scaling system (like BuyBonus and WarningPopup)
const INFO_POPUP_CONFIG = {
  // Reference width for base values (all base values are defined at this width)
  REFERENCE_WIDTH: 1000, // Even wider reference width for info popups

  // Base values at reference width (900px)
  basePadding: 80, // Increased from 60 for more vertical space
  baseMessageFontSize: 52, // Slightly larger font for info messages
  baseNumberFontSize: 70, // Larger for numbers but not too big
  baseBorderRadius: 20,
  baseMinEdgePadding: 20, // Minimum gap from screen edges
  baseContentWidth: 880, // Much wider content width (1100 - 60*2 padding)

  // Device-specific scale multipliers (ONLY scale, everything else follows)
  DEVICE_CONFIGS: {
    [PixiSettings.DEVICE.MOBILE]: {
      [PixiSettings.ORIENTATION.PORTRAIT]: {
        scaleMultiplier: 0.3 // Mobile portrait - keep full size
      },
      [PixiSettings.ORIENTATION.LANDSCAPE]: {
        scaleMultiplier: 0.28, // Mobile landscape - keep full size
      },
    },
    [PixiSettings.DEVICE.TABLET]: {
      [PixiSettings.ORIENTATION.PORTRAIT]: {
        scaleMultiplier: 0.4, // Tablet portrait - 70% size
      },
      [PixiSettings.ORIENTATION.LANDSCAPE]: {
        scaleMultiplier: 0.4, // Tablet landscape - 60% size
      },
    },
    [PixiSettings.DEVICE.DESKTOP]: {
      [PixiSettings.ORIENTATION.LANDSCAPE]: {
        scaleMultiplier: 0.5, // Desktop - 50% size
      },
    },
  },
};

const InfoPopup = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { dimensions, device, orientation } = useDimensions();

  // Redux state
  const infoPopupActive = useSelector(selectInfoPopupActive);
  const messageKey = useSelector(selectInfoPopupKey);
  const infoPopupData = useSelector(selectInfoPopupData);

  const isOpen = DEV_MODE ? true : infoPopupActive;

  const [overlayAlpha, setOverlayAlpha] = useState(0);
  const [isClosing, setIsClosing] = useState(false);
  const popupContentRef = useRef(null);

  // Memoize device config
  const deviceConfig = useMemo(() => {
    return INFO_POPUP_CONFIG.DEVICE_CONFIGS[device]?.[orientation] ||
           INFO_POPUP_CONFIG.DEVICE_CONFIGS[PixiSettings.DEVICE.DESKTOP][PixiSettings.ORIENTATION.LANDSCAPE];
  }, [device, orientation]);

  // Parse message and extract number for special formatting
  // Numbers should be displayed larger and bold on separate line
  const { textBefore, number, textAfter } = useMemo(() => {
    let messageText = "";

    if (messageKey) {
      messageText = t(messageKey);

      // Manually replace placeholders like {count}, {remaining}, etc.
      if (messageText && infoPopupData) {
        Object.keys(infoPopupData).forEach(key => {
          const placeholder = `{${key}}`;
          if (messageText.includes(placeholder)) {
            messageText = messageText.replace(new RegExp(`\\{${key}\\}`, 'g'), infoPopupData[key]);
          }
        });
      }
    }

    // Extract formatted number from message (includes decimals and currency)
    // Match: "number with optional decimals followed by optional currency code"
    // Currency must be 2+ consecutive uppercase letters (EUR, USD, MBT, etc.)
    // Stops before single uppercase letters followed by lowercase (like "Free")
    // Examples: "15", "1.20 EUR", "0.3300 MBT C", "8 Free" → captures only "8"
    // But NOT at the start (to avoid error codes like "204 -")
    const numberMatch = messageText.match(/\S+\s+([\d.,]+(?:\s+[A-Z]{2,})*)/);

    // Clean up the matched number (remove trailing spaces)
    const cleanedMatch = numberMatch ? numberMatch[1].trim() : null;

    if (cleanedMatch) {
      const numberIndex = messageText.indexOf(cleanedMatch);
      return {
        textBefore: messageText.substring(0, numberIndex).trim(),
        number: cleanedMatch,
        textAfter: messageText.substring(numberIndex + cleanedMatch.length).trim(),
      };
    }

    // No number found (or number is at start like error codes), return full message as textBefore
    return {
      textBefore: messageText,
      number: null,
      textAfter: "",
    };
  }, [messageKey, infoPopupData, t]);

  const centerX = useMemo(() => dimensions.width / 2, [dimensions.width]);
  const centerY = useMemo(() => dimensions.height / 2, [dimensions.height]);

  // Calculate popup scale: just device multiplier (no max width constraints)
  const popupScale = useMemo(
    () => deviceConfig.scaleMultiplier || 1.0,
    [deviceConfig]
  );

  // Apply popup scale to ALL base values (single source of truth)
  const padding = useMemo(
    () => INFO_POPUP_CONFIG.basePadding * popupScale,
    [popupScale]
  );
  const messageFontSize = useMemo(
    () => Math.round(INFO_POPUP_CONFIG.baseMessageFontSize * popupScale),
    [popupScale]
  );
  const numberFontSize = useMemo(
    () => Math.round(INFO_POPUP_CONFIG.baseNumberFontSize * popupScale),
    [popupScale]
  );
  const borderRadius = useMemo(
    () => INFO_POPUP_CONFIG.baseBorderRadius * popupScale,
    [popupScale]
  );

  // Fixed content width for consistent text wrapping across all devices
  const contentWidth = useMemo(
    () => INFO_POPUP_CONFIG.baseContentWidth * popupScale,
    [popupScale]
  );

  // Vertical gap between text sections
  const lineGap = useMemo(
    () => messageFontSize * 0.3,
    [messageFontSize]
  );

  // Text styling - MUST be before early return (lighter weight)
  const messageStyle = useMemo(() => new TextStyle({
    fontFamily: FONT_CONFIG.PRIMARY,
    fontSize: messageFontSize,
    fontWeight: "400", // Regular weight (not bold)
    fill: 0xffffff,
    align: "center",
    wordWrap: true,
    wordWrapWidth: contentWidth, // Fixed width for consistent wrapping
    lineHeight: messageFontSize * 1.2, // Better line spacing
    breakWords: false, // Don't break words in the middle
    ...TEXT_STYLE_CONFIG.OUTLINE,
  }), [messageFontSize, contentWidth]);

  // Number styling (larger and bold)
  const numberStyle = useMemo(() => new TextStyle({
    fontFamily: FONT_CONFIG.PRIMARY,
    fontSize: numberFontSize,
    fontWeight: "700", // Bold for numbers
    fill: 0xffffff,
    align: "center",
    ...TEXT_STYLE_CONFIG.OUTLINE,
  }), [numberFontSize]);

  // Convert all text to uppercase
  const textBeforeUpper = useMemo(
    () => textBefore.toUpperCase(),
    [textBefore]
  );
  const numberUpper = useMemo(
    () => number ? number.toUpperCase() : null,
    [number]
  );
  const textAfterUpper = useMemo(
    () => textAfter.toUpperCase(),
    [textAfter]
  );

  // Calculate text heights using TextMetrics - MUST be before early return
  const textBeforeMetrics = useMemo(() => {
    return TextMetrics.measureText(textBeforeUpper, messageStyle);
  }, [textBeforeUpper, messageStyle]);

  const numberMetrics = useMemo(() => {
    return numberUpper ? TextMetrics.measureText(numberUpper, numberStyle) : { height: 0 };
  }, [numberUpper, numberStyle]);

  const textAfterMetrics = useMemo(() => {
    return TextMetrics.measureText(textAfterUpper, messageStyle);
  }, [textAfterUpper, messageStyle]);

  // Calculate total content height (text before + number + text after + gaps)
  const totalContentHeight = useMemo(
    () => textBeforeMetrics.height +
      (number ? lineGap + numberMetrics.height + lineGap : 0) +
      textAfterMetrics.height,
    [textBeforeMetrics.height, number, lineGap, numberMetrics.height, textAfterMetrics.height]
  );

  // Calculate panel dimensions (fixed width, dynamic height based on text)
  const panelWidth = useMemo(
    () => contentWidth + padding * 2,
    [contentWidth, padding]
  );
  const panelHeight = useMemo(
    () => totalContentHeight + padding * 2,
    [totalContentHeight, padding]
  );

  // Center the panel perfectly in the middle of the screen
  const panelX = useMemo(
    () => centerX - panelWidth / 2,
    [centerX, panelWidth]
  );
  const panelY = useMemo(
    () => centerY - panelHeight / 2,
    [centerY, panelHeight]
  );

  // Position content - calculate Y positions for each text section
  const { textBeforeY, numberY, textAfterY } = useMemo(() => {
    if (!number) {
      // Single text block, centered vertically in the panel
      return {
        textBeforeY: centerY,
        numberY: 0,
        textAfterY: 0,
      };
    } else {
      // Multiple text sections with number
      const contentStartY = panelY + padding;
      return {
        textBeforeY: contentStartY + textBeforeMetrics.height / 2,
        numberY: contentStartY + textBeforeMetrics.height + lineGap + numberMetrics.height / 2,
        textAfterY: contentStartY + textBeforeMetrics.height + lineGap + numberMetrics.height + lineGap + textAfterMetrics.height / 2,
      };
    }
  }, [number, centerY, panelY, padding, textBeforeMetrics.height, lineGap, numberMetrics.height, textAfterMetrics.height]);

  // Handle overlay fade in, popup scale up, and UI disable when opening
  useEffect(() => {
    if (isOpen && !isClosing) {
      // Disable UI when popup opens
      dispatch(setUserInterfaceDisabled(true));

      // 1. Fade in overlay
      anime({
        targets: { alpha: 0 },
        alpha: OVERLAY_BACKGROUND_ALPHA,
        duration: OVERLAY_FADE_DURATION,
        easing: "easeOutQuad",
        update: (anim) => {
          setOverlayAlpha(anim.animations[0].currentValue);
        },
      });

      // 2. Scale up popup (with slight delay, starts during overlay fade)
      if (popupContentRef.current) {
        anime({
          targets: popupContentRef.current.scale,
          x: [0, 1],
          y: [0, 1],
          duration: POPUP_SCALE_DURATION,
          delay: POPUP_SCALE_DELAY,
          easing: POPUP_SCALE_EASING,
        });
      }
    } else if (!isOpen && !isClosing) {
      // Re-enable UI when popup closed externally (no animation needed)
      dispatch(setUserInterfaceDisabled(false));
    }
  }, [isOpen, isClosing, dispatch]);

  // Handle close animation when user clicks - reverse of open (fade overlay, scale down popup)
  useEffect(() => {
    if (!isClosing) return;

    let overlayAnim = null;
    let popupAnim = null;

    // 1. Scale down popup (reverse of scale up)
    if (popupContentRef.current) {
      popupAnim = anime({
        targets: popupContentRef.current.scale,
        x: 0,
        y: 0,
        duration: POPUP_SCALE_DURATION,
        easing: "easeInBack",
      });
    }

    // 2. Fade out overlay (can run in parallel)
    overlayAnim = anime({
      targets: { alpha: OVERLAY_BACKGROUND_ALPHA },
      alpha: 0,
      duration: OVERLAY_FADE_DURATION,
      delay: 50, // Slight delay so overlay starts fading as popup scales down
      easing: "easeInQuad",
      update: (anim) => {
        setOverlayAlpha(anim.animations[0].currentValue);
      },
      complete: () => {
        setOverlayAlpha(0);
        dispatch(setUserInterfaceDisabled(false));
        dispatch(closeInfoPopup());
        setIsClosing(false);
      },
    });

    return () => {
      if (overlayAnim) overlayAnim.pause();
      if (popupAnim) popupAnim.pause();
    };
  }, [isClosing, dispatch]);

  const handleClose = useCallback(() => {
    if (isClosing) return;
    
    // Play click sound
    soundManager.play(SOUND_NAME.button_click);
    
    setIsClosing(true);
  }, [isClosing]);

  // Memoize Graphics draw functions
  const drawOverlay = useCallback((g) => {
    g.clear();
    g.beginFill(OVERLAY_BACKGROUND_COLOR);
    g.drawRect(0, 0, dimensions.width, dimensions.height);
    g.endFill();
  }, [dimensions.width, dimensions.height]);

  const drawPanel = useCallback((g) => {
    g.clear();
    g.beginFill(PANEL_BACKGROUND_COLOR, PANEL_BACKGROUND_ALPHA);
    g.drawRoundedRect(
      panelX,
      panelY,
      panelWidth,
      panelHeight,
      borderRadius
    );
    g.endFill();
  }, [panelX, panelY, panelWidth, panelHeight, borderRadius]);

  // Early return AFTER all hooks - stay mounted during close animation
  if (!isOpen && !isClosing) {
    return null;
  }

  return (
    <Container name="info_popup" zIndex={1005} sortableChildren={true}>
      {/* Black overlay - clickable to close */}
      <Graphics
        draw={drawOverlay}
        alpha={overlayAlpha}
        eventMode="static"
        cursor="pointer"
        pointerdown={(e) => {
          e.stopPropagation();
          handleClose();
        }}
        zIndex={1005}
      />

      {/* Content container - animated scale, clickable to close */}
      <Container 
        name="popup_content" 
        ref={popupContentRef} 
        zIndex={1006}
        x={centerX}
        y={centerY}
        pivot={{ x: centerX, y: centerY }}
        eventMode="static"
        cursor="pointer"
        pointerdown={(e) => {
          e.stopPropagation();
          handleClose();
        }}
      >
        {/* Dark panel background - wraps text only */}
        <Graphics
          draw={drawPanel}
        />

        {/* Text before number */}
        <Text
          text={textBeforeUpper}
          style={messageStyle}
          anchor={0.5}
          x={centerX}
          y={textBeforeY}
          resolution={2}
        />

        {/* Number (bold and larger) */}
        {numberUpper && (
          <Text
            text={numberUpper}
            style={numberStyle}
            anchor={0.5}
            x={centerX}
            y={numberY}
            resolution={2}
          />
        )}

        {/* Text after number */}
        {textAfterUpper && (
          <Text
            text={textAfterUpper}
            style={messageStyle}
            anchor={0.5}
            x={centerX}
            y={textAfterY}
            resolution={2}
          />
        )}
      </Container>
    </Container>
  );
};

export default InfoPopup;
